import React, { useEffect, useState, useCallback, useRef } from 'react';
import { FaGlobe, FaChevronDown, FaCheck } from 'react-icons/fa';

const COOKIE_NAME = 'googtrans';
const DEFAULT_LANG = 'fr';

const LANGUAGES = [
    { code: 'fr', label: 'Français', short: 'Fr' },
    { code: 'en', label: 'English',  short: 'En' },
];

function readLangFromCookie() {
    const match = document.cookie.match(new RegExp(`${COOKIE_NAME}=([^;]+)`));
    if (!match) return null;
    const parts = match[1].split('/').filter(Boolean);
    return parts[parts.length - 1] || null;
}

function hideGoogleElements() {
    const selectors = [
        'iframe.skiptranslate',
        '.VIpgJd-ZVi9od-ORHb',
        '.goog-te-banner-frame',
        '.goog-te-gadget',
    ];
    selectors.forEach((sel) => {
        document.querySelectorAll(sel).forEach((el) => {
            el.style.cssText =
                'display:none!important;visibility:hidden!important;height:0!important;width:0!important;overflow:hidden!important;';
        });
    });
    document.body.style.top = '0px';
}

const GoogleTranslate = ({ className = '' }) => {
    const [activeLang, setActiveLang] = useState(
        readLangFromCookie() || DEFAULT_LANG
    );
    const [gtReady, setGtReady]   = useState(false);
    const [isOpen,  setIsOpen]    = useState(false);
    const dropdownRef             = useRef(null);

    const activeLangObj = LANGUAGES.find((l) => l.code === activeLang) || LANGUAGES[0];

    // ── Injection du script Google Translate ─────────────────────────────
    useEffect(() => {
        if (document.getElementById('gt-script')) {
            setGtReady(true);
            return;
        }

        window.googleTranslateElementInit = () => {
            // eslint-disable-next-line no-new
            new window.google.translate.TranslateElement(
                {
                    pageLanguage: DEFAULT_LANG,
                    includedLanguages: LANGUAGES.map((l) => l.code).join(','),
                    layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
                    autoDisplay: false,
                },
                'gt-hidden-element'
            );
            setGtReady(true);
            setTimeout(hideGoogleElements, 300);
        };

        const script    = document.createElement('script');
        script.id       = 'gt-script';
        script.src      = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
        script.async    = true;
        document.head.appendChild(script);
    }, []);

    // ── MutationObserver pour masquer les ajouts dynamiques ──────────────
    useEffect(() => {
        const observer = new MutationObserver(() => hideGoogleElements());
        observer.observe(document.body, { childList: true, subtree: true });
        return () => observer.disconnect();
    }, []);

    // ── Fermer le dropdown si clic en dehors ─────────────────────────────
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // ── Changement de langue ─────────────────────────────────────────────
    const switchLanguage = useCallback(
        (lang) => {
            setIsOpen(false);
            if (lang === activeLang) return;
            setActiveLang(lang);

            // Méthode 1 : via le combo natif
            if (gtReady && window.google?.translate) {
                const combo = document.querySelector('.goog-te-combo');
                if (combo) {
                    combo.value = lang;
                    combo.dispatchEvent(new Event('change'));
                    setTimeout(hideGoogleElements, 500);
                    return;
                }
            }

            // Méthode 2 : cookie + reload (fallback)
            document.cookie = `${COOKIE_NAME}=/auto/${lang}; path=/`;
            window.location.reload();
        },
        [activeLang, gtReady]
    );

    return (
        <>
            {/* Conteneur caché requis par Google Translate */}
            <div id="gt-hidden-element" style={{ display: 'none' }} />

            {/* Dropdown personnalisé */}
            <div
                ref={dropdownRef}
                className={`gt-lang-dropdown ${className}`}
                style={{ position: 'relative', display: 'inline-block' }}
            >
                {/* Bouton déclencheur */}
                <button
                    onClick={() => setIsOpen((prev) => !prev)}
                    className="gt-lang-trigger"
                    title="Changer la langue"
                >
                    <FaGlobe className="gt-globe-icon" />
                    <span className="gt-lang-label">{activeLangObj.short}</span>
                    <FaChevronDown
                        className="gt-chevron"
                        style={{
                            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s ease',
                        }}
                    />
                </button>

                {/* Liste déroulante */}
                {isOpen && (
                    <ul className="gt-lang-menu">
                        {LANGUAGES.map((lang) => (
                            <li key={lang.code}>
                                <button
                                    onClick={() => switchLanguage(lang.code)}
                                    className={`gt-lang-option ${activeLang === lang.code ? 'gt-lang-active' : ''}`}
                                >
                                    <span>{lang.label}</span>
                                    {activeLang === lang.code && (
                                        <FaCheck className="gt-check-icon" />
                                    )}
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Styles du composant */}
            <style>{`
                /* ── Masquer les éléments natifs Google Translate ── */
                .VIpgJd-ZVi9od-ORHb,
                .skiptranslate,
                .goog-te-banner-frame,
                .goog-te-menu-value,
                .goog-te-menu-frame,
                .goog-te-combo,
                .goog-te-gadget {
                    display: none !important;
                    visibility: hidden !important;
                    height: 0 !important;
                    width: 0 !important;
                    overflow: hidden !important;
                    position: absolute !important;
                    clip: rect(0 0 0 0) !important;
                }
                body { top: 0 !important; }

                /* ── Bouton déclencheur ── */
                .gt-lang-trigger {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    background: rgba(255,255,255,0.15);
                    border: 1px solid rgba(255,255,255,0.35);
                    border-radius: 6px;
                    color: #fff;
                    cursor: pointer;
                    padding: 5px 10px;
                    font-size: 13px;
                    font-weight: 500;
                    transition: background 0.2s;
                    white-space: nowrap;
                }
                .gt-lang-trigger:hover {
                    background: rgba(255,255,255,0.25);
                }
                .gt-globe-icon { font-size: 14px; flex-shrink: 0; }
                .gt-chevron    { font-size: 10px; flex-shrink: 0; }

                /* ── Menu déroulant ── */
                .gt-lang-menu {
                    position: absolute;
                    top: calc(100% + 6px);
                    left: 0;
                    min-width: 130px;
                    background: #fff;
                    border: 1px solid #e0e0e0;
                    border-radius: 8px;
                    box-shadow: 0 6px 20px rgba(0,0,0,0.12);
                    list-style: none;
                    margin: 0;
                    padding: 4px 0;
                    z-index: 9999;
                    animation: gtFadeIn 0.15s ease;
                }
                @keyframes gtFadeIn {
                    from { opacity: 0; transform: translateY(-4px); }
                    to   { opacity: 1; transform: translateY(0); }
                }

                /* ── Option ── */
                .gt-lang-option {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    width: 100%;
                    padding: 8px 14px;
                    background: none;
                    border: none;
                    cursor: pointer;
                    font-size: 13px;
                    color: #333;
                    transition: background 0.15s;
                    text-align: left;
                }
                .gt-lang-option:hover {
                    background: #f5f5f5;
                }
                .gt-lang-option.gt-lang-active {
                    font-weight: 600;
                    color: #1a73e8;
                }
                .gt-check-icon {
                    font-size: 11px;
                    color: #1a73e8;
                    flex-shrink: 0;
                }
            `}</style>
        </>
    );
};

export default GoogleTranslate;